import { useMemo, useState } from 'react';
import { Eye, Plus, RefreshCw, Search, ShoppingCart, Truck } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTableArea,
  CardTitle,
} from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageLoader } from '@/components/ui/Spinner';
import {
  Table,
  TableActionsCell,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { formatDate } from '@/lib/format';
import { orderStatus } from '@/theme/status';
import type { EstadoOrden } from '@/types/domain';
import type { Order, OrderFilters } from '@/types/orders';
import { OrderDetailDialog } from './OrderDetailDialog';
import { OrderDialog } from './OrderDialog';
import { OrderDropiDialog } from './OrderDropiDialog';
import { OrderGuideDialog } from './OrderGuideDialog';
import { OrderStatusDialog } from './OrderStatusDialog';
import {
  canAttachGuide,
  clientSearchText,
  countLabel,
  formatNumber,
  nextEstados,
  orderAddressLines,
  orderItemsSummary,
  summarizeOrders,
} from './orders-format';
import { useOrders } from './use-orders';

/**
 * Órdenes (`/orders`, módulo 7): el tablero del despacho del negocio activo.
 *
 * Es la pantalla donde el admin cierra la venta que el bot ya cerró en el chat: confirma el pedido
 * (`PENDIENTE_CONFIRMACION` → `CONFIRMADA`), lo prepara, carga la foto de la guía —que es lo que
 * marca el despacho y le avisa al cliente— y lo cierra como entregado o cancelado.
 *
 * El backend NO pagina este listado: `GET .../orders` devuelve las últimas `limit` órdenes
 * (default 50, máximo 200). La barra de herramientas trabaja con eso:
 *  - el filtro de estado viaja al servidor (es un query param de verdad) y forma parte de la caché;
 *  - el buscador filtra en el cliente sobre lo que ya está cargado (el endpoint no tiene búsqueda);
 *  - cuando la lista viene llena, la pantalla lo dice: puede haber órdenes más viejas que no se
 *    están viendo, y se sube el límite para traerlas.
 *
 * Permisos: ver órdenes lo puede hacer cualquier miembro (el AGENTE_HUMANO atiende pedidos), pero
 * crear una orden, cambiar el estado y cargar la guía son solo ADMIN (`canEdit`); el backend responde
 * 403 igual si alguien invoca los endpoints a mano.
 */

/** Filtro de la barra de herramientas: `''` = todos los estados. */
type StatusFilter = EstadoOrden | '';

/** Cuántas órdenes se piden al backend (acepta 1-200; 50 es su default). */
const LIMIT_OPTIONS = [25, 50, 100, 200] as const;
const DEFAULT_LIMIT = 50;

export function OrdersPage() {
  const { tenantId, canEdit } = useActiveTenantAccess();

  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState<StatusFilter>('');
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [detail, setDetail] = useState<Order | null>(null);
  const [statusOrder, setStatusOrder] = useState<Order | null>(null);
  const [guideOrder, setGuideOrder] = useState<Order | null>(null);
  /** Orden que se manda a Dropi (docs/DROPI.md); `null` = diálogo cerrado. */
  const [dropiOrder, setDropiOrder] = useState<Order | null>(null);

  /** Filtros que viajan al backend (los mismos que forman la query key). */
  const filters = useMemo<OrderFilters>(
    () => ({ ...(estadoFilter ? { estado: estadoFilter } : {}), limit }),
    [estadoFilter, limit],
  );

  const ordersQuery = useOrders(tenantId, filters);
  const orders = ordersQuery.data ?? [];

  /** Búsqueda sobre lo que ya está en caché (el endpoint no filtra por texto). */
  const visibleOrders = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle.length === 0) return orders;

    return orders.filter((order) => clientSearchText(order).includes(needle));
  }, [orders, search]);

  const filtering = search.trim().length > 0 || estadoFilter !== '';
  /** Hay órdenes, pero nada pasa la búsqueda: se distingue de "todavía no hay órdenes". */
  const noMatches = orders.length > 0 && visibleOrders.length === 0;
  /** La última página vino llena: puede haber órdenes más viejas fuera del límite. */
  const hayMas = orders.length >= limit;

  function clearFilters() {
    setSearch('');
    setEstadoFilter('');
  }

  return (
    <>
      <Card>
        <CardHeader
          actions={
            canEdit ? (
              <Button
                size="sm"
                leftIcon={<Plus size={14} />}
                onClick={() => setDialogOpen(true)}
              >
                Nueva orden
              </Button>
            ) : null
          }
        >
          <CardTitle>Órdenes</CardTitle>
          <CardDescription>
            {summarizeOrders(orders)}. Cada cambio de estado queda con su motivo en el historial, y la
            foto de la guía es lo que marca el despacho: al guardarla la orden pasa a «Enviada» y el
            cliente recibe el aviso en su chat.
            {canEdit
              ? ''
              : ' Tu rol en este negocio es de solo lectura: solo un administrador puede crear órdenes, cambiarlas de estado o cargar la guía.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input
              type="search"
              value={search}
              leftIcon={<Search size={16} />}
              placeholder="Buscar por cliente, teléfono, ciudad, producto o número de guía…"
              aria-label="Buscar órdenes"
              onChange={(event) => setSearch(event.target.value)}
            />

            <Select
              aria-label="Filtrar por estado"
              className="sm:w-56"
              value={estadoFilter}
              onChange={(event) => setEstadoFilter(event.target.value as StatusFilter)}
            >
              <option value="">Todos los estados</option>
              {(Object.keys(orderStatus) as EstadoOrden[]).map((estado) => (
                <option key={estado} value={estado}>
                  {orderStatus[estado].label}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Cuántas órdenes mostrar"
              className="sm:w-44"
              value={String(limit)}
              onChange={(event) => setLimit(Number(event.target.value))}
            >
              {LIMIT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {`Últimas ${option}`}
                </option>
              ))}
            </Select>
          </div>

          {ordersQuery.isPending ? <PageLoader label="Cargando las órdenes…" /> : null}

          {ordersQuery.isError ? <Alert>{getErrorMessage(ordersQuery.error)}</Alert> : null}

          {ordersQuery.isSuccess ? (
            <>
              {orders.length === 0 ? (
                <EmptyState
                  icon={<ShoppingCart size={22} />}
                  title={estadoFilter === '' ? 'Todavía no hay órdenes' : 'Ninguna orden en ese estado'}
                  description={
                    estadoFilter === ''
                      ? 'Las órdenes nacen solas en el chat: cuando el cliente manda el comprobante de pago, el bot crea el pedido y le avisa al admin por WhatsApp. Si la venta se cerró por fuera, cárgala aquí a mano.'
                      : `No hay órdenes en «${orderStatus[estadoFilter].label}» entre las últimas ${limit}.`
                  }
                  action={
                    estadoFilter === '' ? (
                      canEdit ? (
                        <Button leftIcon={<Plus size={16} />} onClick={() => setDialogOpen(true)}>
                          Cargar la primera orden
                        </Button>
                      ) : undefined
                    ) : (
                      <Button variant="secondary" onClick={clearFilters}>
                        Quitar el filtro de estado
                      </Button>
                    )
                  }
                />
              ) : null}

              {noMatches ? (
                <EmptyState
                  icon={<Search size={22} />}
                  title="Ninguna orden coincide"
                  description={
                    search.trim().length > 0
                      ? `Nada en las órdenes cargadas coincide con «${search.trim()}»${
                          estadoFilter === '' ? '' : ` entre las que están en «${orderStatus[estadoFilter].label}»`
                        }.`
                      : 'No hay órdenes con ese filtro.'
                  }
                  action={
                    <Button variant="secondary" onClick={clearFilters}>
                      Quitar los filtros
                    </Button>
                  }
                />
              ) : null}

              {filtering && !noMatches ? (
                <p className="text-caption text-ink-muted">
                  {countLabel(visibleOrders.length, 'orden visible', 'órdenes visibles')} de{' '}
                  {formatNumber(orders.length)}.
                </p>
              ) : null}

              {hayMas && !filtering ? (
                <p className="text-caption text-ink-faint">
                  {`Se están mostrando las últimas ${formatNumber(orders.length)} órdenes: puede haber más antiguas. Sube el límite para verlas.`}
                </p>
              ) : null}

              {visibleOrders.length > 0 ? (
                <CardTableArea>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Cliente</TableHeaderCell>
                        <TableHeaderCell>Pedido</TableHeaderCell>
                        <TableHeaderCell>Entrega</TableHeaderCell>
                        <TableHeaderCell>Estado</TableHeaderCell>
                        <TableHeaderCell>Creada</TableHeaderCell>
                        <TableHeaderCell align="right">Acciones</TableHeaderCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {visibleOrders.map((order) => {
                        const meta = orderStatus[order.estado];
                        const destinos = nextEstados(order.estado);
                        const direccion = orderAddressLines(order);

                        return (
                          <TableRow key={order.id}>
                            <TableCell>
                              <span className="flex max-w-md flex-col gap-0.5">
                                <button
                                  type="button"
                                  onClick={() => setDetail(order)}
                                  className="text-left text-body font-medium text-ink transition-colors hover:text-brand-600"
                                >
                                  {order.clienteNombre}
                                </button>
                                <span className="text-caption text-ink-muted">
                                  {order.telefono ?? 'Sin teléfono'}
                                </span>
                              </span>
                            </TableCell>

                            <TableCell>
                              <span className="flex flex-col gap-0.5">
                                <span className="text-caption text-ink-soft">
                                  {orderItemsSummary(order)}
                                </span>
                                <span className="tabular text-body text-ink">
                                  {formatNumber(order.total)}
                                </span>
                              </span>
                            </TableCell>

                            <TableCell>
                              {direccion.length === 0 ? (
                                <span className="text-caption text-ink-faint">Sin dirección</span>
                              ) : (
                                <span className="text-caption text-ink-soft">
                                  {direccion.join(' · ')}
                                </span>
                              )}
                            </TableCell>

                            <TableCell>
                              <span className="flex flex-col items-start gap-1">
                                <StatusBadge label={meta.label} tone={meta.tone} />
                                {order.guiaEnvioFotoUrl ? (
                                  <span className="text-micro text-ink-faint">
                                    {order.guiaEnvioNumero
                                      ? `Guía ${order.guiaEnvioNumero}`
                                      : 'Con guía cargada'}
                                  </span>
                                ) : null}
                              </span>
                            </TableCell>

                            <TableCell className="whitespace-nowrap text-caption text-ink-muted">
                              {formatDate(order.createdAt)}
                            </TableCell>

                            <TableActionsCell>
                              <span className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Ver la orden de ${order.clienteNombre}`}
                                  onClick={() => setDetail(order)}
                                >
                                  <Eye size={16} />
                                </Button>

                                {canEdit && destinos.length > 0 ? (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Cambiar el estado de la orden de ${order.clienteNombre}`}
                                    onClick={() => setStatusOrder(order)}
                                  >
                                    <RefreshCw size={16} />
                                  </Button>
                                ) : null}

                                {canEdit && canAttachGuide(order.estado) ? (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Cargar la guía de la orden de ${order.clienteNombre}`}
                                    onClick={() => setGuideOrder(order)}
                                  >
                                    <Truck size={16} />
                                  </Button>
                                ) : null}
                              </span>
                            </TableActionsCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardTableArea>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>

      {canEdit ? <OrderDialog open={dialogOpen} onClose={() => setDialogOpen(false)} /> : null}

      {/* Ficha de solo lectura: se abre con el nombre o con el ojo de la fila. El detalle se vuelve a
          leer del endpoint (`useOrder`) y los botones de escritura solo aparecen si el usuario es
          ADMIN y la orden admite esa acción. */}
      <OrderDetailDialog
        open={detail !== null}
        order={detail}
        onClose={() => setDetail(null)}
        onChangeStatus={
          canEdit
            ? (order) => {
                setDetail(null);
                setStatusOrder(order);
              }
            : undefined
        }
        onAttachGuide={
          canEdit
            ? (order) => {
                setDetail(null);
                setGuideOrder(order);
              }
            : undefined
        }
        onSendToDropi={
          canEdit
            ? (order) => {
                setDetail(null);
                setDropiOrder(order);
              }
            : undefined
        }
      />

      {/* Los diálogos de escritura se montan solo para ADMIN (el backend responde 403 igual si
          alguien invoca los endpoints a mano). */}
      {canEdit ? (
        <OrderStatusDialog
          open={statusOrder !== null}
          order={statusOrder}
          onClose={() => setStatusOrder(null)}
        />
      ) : null}

      {canEdit ? (
        <OrderGuideDialog
          open={guideOrder !== null}
          order={guideOrder}
          onClose={() => setGuideOrder(null)}
        />
      ) : null}

      {canEdit ? (
        <OrderDropiDialog
          open={dropiOrder !== null}
          order={dropiOrder}
          onClose={() => setDropiOrder(null)}
        />
      ) : null}
    </>
  );
}
